/**
 * useObstructionAnchorGeoJson Hook
 *
 * Converts the obstruction map items into a GeoJSON FeatureCollection
 * suitable for the unified `map-cluster-source`. Each feature is a `Point`
 * anchored at `ObstructionMapItem.anchorPoint`, tagged with
 * `featureKind === 'obstruction'` so the obstruction symbol layer can pick
 * them up via `buildUnclusteredFeatureFilter`.
 *
 * Filtering semantics:
 *   - When the obstructions artifact layer is hidden, returns an empty
 *     FeatureCollection (so the cluster source no longer counts them).
 *   - When an obstruction is being edited, the matching feature is removed
 *     from the collection so the AnchorMarker shows alone — mirrors how the
 *     task layer hides the currently-editing task.
 *   - The active obstruction filter slice values are applied client-side so
 *     the map view honours the same filters as the list view (the API
 *     `getForMap` endpoint currently returns every obstruction).
 *
 * Atomic-selector rule: subscribes to a small set of primitives + uses
 * `useShallow` for the multi-value filter object. The returned FeatureCollection
 * is memoised by a content signature so identical data avoids recomputation.
 *
 * @module components/map/UnifiedAnchorSymbolLayer/hooks/useObstructionAnchorGeoJson
 */

import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import type { Feature, FeatureCollection, Point } from 'geojson';

import { useGeoMarkupStore } from '@/store';
import { selectArtifactObstructionsVisible } from '@/store/selectors';
import {
  MAP_CLUSTER_FEATURE_KIND_OBSTRUCTION,
  type MapClusterFeatureKind,
} from '@/components/map/MapClusterLayer/featureKind';
import {
  ObstructionStatus,
  ObstructionType,
  SeverityLevel,
  TrafficImpact,
} from '@/types/obstructions';
import type { ObstructionMapItem } from '@/types/obstructions';

// ===== Types =====

/**
 * Per-feature properties exposed to the obstruction symbol layer + click /
 * hover handlers. All values are primitives so they survive MapLibre's
 * `queryRenderedFeatures` JSON serialisation roundtrip.
 */
export interface ObstructionFeatureProperties {
  /** Discriminator for the unified cluster source. */
  featureKind: MapClusterFeatureKind;
  /** Obstruction unique identifier (GUID). */
  obstructionId: string;
  /** Mirrors `obstructionId` under the conventional `id` key used by the
   *  shared marker-hover hook (`useMapMarkerHoverPreview`). */
  id: string;
  /** URL-friendly short identifier (NanoID). */
  shortId: string;
  /** Obstruction type — drives sprite selection. */
  type: ObstructionType;
  /** Lifecycle status. */
  status: ObstructionStatus;
  /** Severity level — drives halo color. */
  severity: SeverityLevel;
  /** Traffic impact. */
  impact: TrafficImpact;
  /** Obstruction title — surfaced by future hover-preview consumers. */
  title: string;
}

// ===== Helpers =====

function obstructionToFeature(
  obstruction: ObstructionMapItem
): Feature<Point, ObstructionFeatureProperties> {
  return {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [obstruction.anchorPoint.lng, obstruction.anchorPoint.lat],
    },
    properties: {
      featureKind: MAP_CLUSTER_FEATURE_KIND_OBSTRUCTION,
      obstructionId: obstruction.id,
      id: obstruction.id,
      shortId: obstruction.shortId,
      type: obstruction.type,
      status: obstruction.status,
      severity: obstruction.severity,
      impact: obstruction.impact,
      title: obstruction.title,
    },
  };
}

interface ObstructionFilterSnapshot {
  search: string;
  types: ObstructionType[];
  statuses: ObstructionStatus[];
  severities: SeverityLevel[];
  impacts: TrafficImpact[];
  location: string;
  contractor: string;
}

const EMPTY_FEATURE_COLLECTION: FeatureCollection<
  Point,
  ObstructionFeatureProperties
> = {
  type: 'FeatureCollection',
  features: [],
};

/**
 * Applies the active obstruction filter slice values client-side. The map
 * endpoint returns the full list so the filter slice is the canonical
 * authority on visibility; `location` / `contractor` filters fall back to a
 * `true` predicate because `ObstructionMapItem` does not carry that
 * metadata — they only affect the list view.
 */
function matchesFilters(
  obstruction: ObstructionMapItem,
  filters: ObstructionFilterSnapshot
): boolean {
  if (filters.search) {
    const term = filters.search.toLowerCase();
    if (!obstruction.title.toLowerCase().includes(term)) return false;
  }
  if (filters.types.length > 0 && !filters.types.includes(obstruction.type)) {
    return false;
  }
  if (
    filters.statuses.length > 0 &&
    !filters.statuses.includes(obstruction.status)
  ) {
    return false;
  }
  if (
    filters.severities.length > 0 &&
    !filters.severities.includes(obstruction.severity)
  ) {
    return false;
  }
  if (
    filters.impacts.length > 0 &&
    !filters.impacts.includes(obstruction.impact)
  ) {
    return false;
  }
  return true;
}

// ===== Hook =====

/**
 * Builds the obstruction `FeatureCollection` for the unified cluster source.
 *
 * Returns an empty FeatureCollection when the artifact layer is toggled off
 * so the cluster source does not count hidden obstructions.
 */
export function useObstructionAnchorGeoJson(): FeatureCollection<
  Point,
  ObstructionFeatureProperties
> {
  const visible = useGeoMarkupStore(selectArtifactObstructionsVisible);
  const mapItems = useGeoMarkupStore(
    useShallow((state) => state.obstructions.mapItems)
  );
  const editingId = useGeoMarkupStore((state) =>
    state.obstructions.formIsOpen ? state.obstructions.formEditingId : null
  );
  const filters: ObstructionFilterSnapshot = useGeoMarkupStore(
    useShallow((state) => ({
      search: state.obstructionFilters.search,
      types: state.obstructionFilters.types,
      statuses: state.obstructionFilters.statuses,
      severities: state.obstructionFilters.severities,
      impacts: state.obstructionFilters.impacts,
      location: state.obstructionFilters.location,
      contractor: state.obstructionFilters.contractor,
    }))
  );

  return useMemo(() => {
    if (!visible) return EMPTY_FEATURE_COLLECTION;

    const visibleItems = mapItems.filter((item) => {
      if (editingId !== null && item.id === editingId) return false;
      return matchesFilters(item, filters);
    });

    return {
      type: 'FeatureCollection',
      features: visibleItems.map(obstructionToFeature),
    };
  }, [visible, mapItems, editingId, filters]);
}
